
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// module components
import StoryFormSlide from '@/modules/StoriesModule/components/StoryFormSlide/index.vue'

// types
import { StorySlideT, StoryT } from '@/core/types/stories.types'

// realisations
import { stories } from '@/core/realisations/stories.realisation'

// constants
import { VISIBILITY_TYPES } from '@/core/constants/stories.constants'

export default defineComponent({
    components: {
        'the-button': TheButton,
        'the-select': TheSelect,
        'the-input': TheInput,
        'file-reader': FileReader,
        'story-form-slide': StoryFormSlide,
        loader: Loader,
    },
    props: {
        isCreate: {
            type: Boolean,
            default: true,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        story: {
            type: Object as PropType<StoryT>,
            default: null,
        },
    },
    emits: ['cancel', 'saved'],
    setup(props, { emit }) {
        const state = reactive({
            basic: {
                id: null as number | null,
                visibilityType: null as number | null,
                name: null as string | null,
                image: null as string | null,
                images: [] as File[],
                published: false,
                is_saving: false,
                is_saved: false,
            },
            is_slides_loading: false,
            slides: [] as StorySlideT[],
        })

        const slide_form = reactive({
            images: [] as File[],
            is_creating: false,
        })

        const formTitle = computed(() => {
            if (props.isCreate) return 'Добавление истории'
            if (props.isUpdate) return 'Редактирование истории'
            return 'form title'
        })

        const loadSlides = async (story_id: number) => {
            try {
                state.is_slides_loading = true
                const response = await stories.getStorySlides(story_id)
                state.slides = response.data.items
            } catch (error) {
                console.error(error)
            } finally {
                state.is_slides_loading = false
            }
        }

        onBeforeMount(async () => {
            if (!props.isUpdate || !props.story) return

            state.basic.id = props.story.id
            state.basic.name = props.story.name
            state.basic.visibilityType = props.story.visibility_type
            state.basic.image = props.story.image
            state.basic.published = props.story.published

            await loadSlides(props.story.id)
            state.basic.is_saved = true
        })

        const setCover = () => {
            const image_url = URL.createObjectURL(state.basic.images[0])
            state.basic.image = image_url
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const createStory = async () => {
            if (!state.basic.name) return

            try {
                state.basic.is_saving = true

                const payload = new FormData()
                payload.append('name', state.basic.name)
                if (state.basic.visibilityType)
                    payload.append('visibilityType', state.basic.visibilityType.toString())
                payload.append('image', state.basic.images[0])

                const response = await stories.createStory(payload)
                state.basic.id = response.data.id
                state.basic.image = response.data.image
                state.basic.images = []

                state.basic.is_saved = true
                emit('saved')
            } catch (error) {
                console.error(error)
            } finally {
                state.basic.is_saving = false
            }
        }

        const updateStory = async () => {
            if (!state.basic.name || !state.basic.id) return

            try {
                state.basic.is_saving = true

                const payload = new FormData()
                payload.append('name', state.basic.name)
                if (state.basic.visibilityType)
                    payload.append('visibilityType', state.basic.visibilityType.toString())
                payload.append('image', state.basic.images[0])

                const response = await stories.updateStory(state.basic.id, payload)
                state.basic.id = response.data.id
                state.basic.image = response.data.image
                state.basic.images = []

                state.basic.is_saved = true
                emit('saved')
            } catch (error) {
                console.error(error)
            } finally {
                state.basic.is_saving = false
            }
        }

        const createSlide = async () => {
            if (!state.basic.id) return

            try {
                slide_form.is_creating = true

                const payload = new FormData()
                payload.append('image', slide_form.images[0])

                const response = await stories.createStorySlide(state.basic.id, payload)
                state.slides.unshift(response.data)

                slide_form.images = []
            } catch (error) {
                console.error(error)
            } finally {
                slide_form.is_creating = false
            }
        }

        const updateSlide = ({ index, image }: { index: number; image: string }) => {
            state.slides[index].image = image
        }

        const deleteSlide = ({ index }: { index: number }) => {
            state.slides.splice(index, 1)
        }

        const saveHandler = () => {
            if (props.isCreate) {
                createStory()
                return
            }

            if (props.isUpdate) {
                updateStory()
                return
            }
        }

        return {
            state,
            formTitle,
            setCover,
            emitCancel,
            saveHandler,
            slide_form,
            createSlide,
            updateSlide,
            deleteSlide,
            //
            VISIBILITY_TYPES,
        }
    },
})
